import { storeAssigned } from "./normalMethods";
import localStorageCall from "./localstorage.hook";

const SESSION_PREFIX = "payment_callback_session_";
const ACTIVE_SESSION_KEY = "payment_callback_active_session";
const SESSION_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

function generateSessionId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function readSession(sessionId) {
    if (!sessionId) return null;
    try {
        const raw = localStorage.getItem(`${SESSION_PREFIX}${sessionId}`);
        if (!raw) return null;
        const session = JSON.parse(raw);
        if (!session?.createdAt || Date.now() - session.createdAt > SESSION_TTL_MS) {
            clearPaymentCallbackSession(sessionId);
            return null;
        }
        return session;
    } catch {
        return null;
    }
}

function writeSession(sessionId, session) {
    localStorage.setItem(`${SESSION_PREFIX}${sessionId}`, JSON.stringify(session));
    localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
}

/** Create or refresh the active callback session before loading the payment form. */
export function ensurePaymentCallbackSession(customerId = 0) {
    const existingId = localStorage.getItem(ACTIVE_SESSION_KEY);
    const existing = readSession(existingId);
    const resolvedCustomerId = Number(customerId) || existing?.customerId || 0;

    if (existingId && existing) {
        writeSession(existingId, {
            ...existing,
            customerId: resolvedCustomerId,
            store: storeAssigned(),
        });
        return existingId;
    }

    const sessionId = generateSessionId();
    writeSession(sessionId, {
        customerId: resolvedCustomerId,
        store: storeAssigned(),
        createdAt: Date.now(),
    });
    return sessionId;
}

/** Attach the customer who initiated payment before Teqtank redirect/charge. */
export function updatePaymentCallbackSession(customerId) {
    const sessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
    const session = readSession(sessionId);
    if (!sessionId || !session) return null;

    writeSession(sessionId, {
        ...session,
        customerId: Number(customerId) || session.customerId,
    });
    return sessionId;
}

export function getPaymentCallbackSession(sessionId) {
    return readSession(sessionId);
}

export function clearPaymentCallbackSession(sessionId) {
    if (!sessionId) return;
    localStorage.removeItem(`${SESSION_PREFIX}${sessionId}`);
    if (localStorage.getItem(ACTIVE_SESSION_KEY) === sessionId) {
        localStorage.removeItem(ACTIVE_SESSION_KEY);
    }
}

export function buildPaymentCallbackUrl(sessionId) {
    if (typeof window === "undefined" || !sessionId) return "";
    return `${window.location.origin}/${storeAssigned()}/payment-callback?s=${encodeURIComponent(sessionId)}`;
}

/** Resolve customerId for finalize: session is required; logged-in user must match session owner. */
export function resolveCallbackCustomerId(sessionId) {
    const session = getPaymentCallbackSession(sessionId);
    if (!session?.customerId) return { customerId: 0, session: null, error: "invalid_session" };

    const loggedInCustomerId = Number(localStorageCall()?.getSingleItem("Token") || 0);
    if (loggedInCustomerId && loggedInCustomerId !== session.customerId) {
        return { customerId: 0, session, error: "customer_mismatch" };
    }

    return { customerId: session.customerId, session, error: null };
}
